import React from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import type { FieldValues } from 'react-hook-form';
import type { UseFormReturn } from 'react-hook-form/dist/types';
import type { FieldPath } from 'react-hook-form/dist/types/path';
import type { FormFieldVariant } from '../enums';
import { FormInputFieldViewVariant } from '../enums';
import FormField from '../FormField/FormField';
import type { FormFieldProps, FormFieldType } from '../types';
import { FieldType } from '../types';

/**
 * Check field specification to render a React Component that is passed as an argument. NameT is the name of the field and DataT is the definition
 * of bean that holds all the fields for the form.
 */
export interface PlaceholderFormFieldSpec<NameT extends FieldPath<DataT>, DataT extends FieldValues>
  extends FormFieldType<NameT, DataT, FormFieldVariant.PLACEHOLDER_FORM_FIELD> {
  /**
   * Accepts a React Component.
   */
  Content: React.ComponentType<React.PropsWithChildren<{ formMethods: UseFormReturn<DataT> }>>;
}

/**
 * Renders React Component as a field for the form
 *
 * @author Akash Goenka
 */
const PlaceholderFormField = <NameT extends FieldPath<DataT>, DataT extends FieldValues>({
  fieldSpec,
  formId,
  i18n,
  className,
  formMethods,
  onFormSubmit,
  submitOnChange,
  allFields
}: FormFieldProps<NameT, DataT, PlaceholderFormFieldSpec<NameT, DataT>>): React.ReactElement => {
  const cx = useClassNameMapper();
  const { name, Content } = fieldSpec;
  return (
    <FormField
      fieldSpec={fieldSpec}
      fieldType={FieldType.CONTROLLED}
      formId={formId}
      i18n={i18n}
      formMethods={formMethods}
      onFormSubmit={onFormSubmit}
      submitOnChange={submitOnChange}
      allFormFields={allFields}
      className={cx(
        {
          'lia-g-form-field-no-border': FormInputFieldViewVariant.NO_BORDER
        },
        className
      )}
    >
      {({
        render: {
          field: { onChange }
        }
      }) => {
        return (
          <>
            <Content
              aria-label={name}
              data-testid={`PlaceholderFormField.${formId}.${name}`}
              formMethods={formMethods}
            />
            <input
              data-testid={'placeholder-hidden-field'}
              type={'hidden'}
              disabled
              defaultValue={''}
              onChange={() => onChange('')}
            />
          </>
        );
      }}
    </FormField>
  );
};

export default PlaceholderFormField;
